import { apiRequest } from '../lib/api-client';

/** Automations and their runs (context.md sections 84-90). */

export type AutomationStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
export type RunStatus =
  'PENDING' | 'RUNNING' | 'WAITING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'STOPPED';

export interface AutomationView {
  id: string;
  name: string;
  description: string | null;
  status: AutomationStatus;
  triggerType: string;
  publishedVersionId: string | null;
  runCount: number;
  failureCount: number;
  lastRunAt: string | null;
  version: number;
  createdAt: string;
}

export interface RunView {
  id: string;
  status: RunStatus;
  triggerEventId: string | null;
  resumeAt: string | null;
  depth: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export function listAutomations(status?: AutomationStatus): Promise<{
  automations: AutomationView[];
}> {
  return apiRequest(`/v1/automations${status ? `?status=${status}` : ''}`);
}

export function listRuns(automationId: string, status?: RunStatus): Promise<{ runs: RunView[] }> {
  return apiRequest(
    `/v1/automations/${automationId}/runs?limit=50${status ? `&status=${status}` : ''}`,
  );
}

export function pauseAutomation(id: string): Promise<AutomationView> {
  return apiRequest(`/v1/automations/${id}/pause`, { method: 'POST' });
}

export function activateAutomation(id: string): Promise<AutomationView> {
  return apiRequest(`/v1/automations/${id}/activate`, { method: 'POST', body: {} });
}

// ---------------------------------------------------------------------------
// Authoring (Phase 38)
// ---------------------------------------------------------------------------

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'is_empty'
  | 'is_not_empty'
  | 'greater_than'
  | 'less_than'
  | 'in'
  | 'not_in'
  | 'changed_to'
  | 'changed_from';

export const CONDITION_OPERATORS: ConditionOperator[] = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'is_empty',
  'is_not_empty',
  'greater_than',
  'less_than',
  'in',
  'not_in',
  'changed_to',
  'changed_from',
];

/** The two operators that need no value: asking for one would be asking for nothing. */
export const VALUELESS_OPERATORS = new Set<ConditionOperator>(['is_empty', 'is_not_empty']);

export interface Condition {
  field: string;
  operator: ConditionOperator;
  value?: unknown;
}

export interface ConditionGroup {
  match: 'ALL' | 'ANY';
  conditions: Condition[];
}

export type StepType = 'ACTION' | 'CONDITION' | 'WAIT' | 'STOP';

export interface WaitSpec {
  minutes?: number;
  hours?: number;
  days?: number;
  untilBusinessHours?: boolean;
}

export interface Step {
  id: string;
  type: StepType;
  action?: string;
  config?: Record<string, unknown>;
  conditions?: ConditionGroup;
  then?: Step[];
  else?: Step[];
  wait?: WaitSpec;
}

export interface AutomationDefinition {
  steps: Step[];
}

export interface AutomationDetail extends AutomationView {
  triggerConditions: ConditionGroup | null;
  definition: AutomationDefinition;
  definitionVersion: number | null;
  versions: { id: string; version: number; notes: string | null; createdAt: string }[];
}

export interface ActionField {
  key: string;
  kind: 'text' | 'textarea' | 'number' | 'select' | 'agent' | 'user' | 'color' | 'json';
  required: boolean;
  options?: string[];
  maxLength?: number;
}

export interface ActionSpec {
  name: string;
  requires: string[];
  fields: ActionField[];
}

export interface TriggerSpec {
  type: string;
  area: string;
  fields: string[];
}

export function listActions(): Promise<{ actions: ActionSpec[] }> {
  return apiRequest('/v1/automations/actions');
}

export function listTriggers(): Promise<{ triggers: TriggerSpec[] }> {
  return apiRequest('/v1/automations/triggers');
}

export function getAutomation(id: string): Promise<AutomationDetail> {
  return apiRequest(`/v1/automations/${id}`);
}

export function createAutomation(input: {
  name: string;
  description?: string | null;
  triggerType: string;
  triggerConditions?: ConditionGroup;
  definition?: AutomationDefinition;
}): Promise<AutomationDetail> {
  return apiRequest('/v1/automations', { method: 'POST', body: input });
}

/**
 * Saves a draft of the definition.
 *
 * `expectedVersion` travels with it: two people editing the same automation must not silently
 * overwrite each other, and an automation is a standing instruction acting on real records, so
 * the losing edit has to be told rather than dropped (context.md section 199).
 */
export function saveAutomation(
  id: string,
  input: {
    name?: string;
    description?: string | null;
    triggerType?: string;
    triggerConditions?: ConditionGroup;
    definition?: AutomationDefinition;
    notes?: string | null;
    expectedVersion?: number;
  },
): Promise<AutomationDetail> {
  return apiRequest(`/v1/automations/${id}`, { method: 'PATCH', body: input });
}

export function deleteAutomation(id: string): Promise<void> {
  return apiRequest(`/v1/automations/${id}`, { method: 'DELETE' });
}

/** A step id that reads in a log: `act-3`, not a uuid nobody can say out loud. */
export function newStepId(type: StepType, taken: Set<string>): string {
  const prefix = { ACTION: 'act', CONDITION: 'if', WAIT: 'wait', STOP: 'stop' }[type];

  for (let n = 1; n < 500; n += 1) {
    const id = `${prefix}-${n}`;
    if (!taken.has(id)) return id;
  }

  return `${prefix}-${Date.now()}`;
}

/** Every id already used in a tree, so a new step cannot collide with one inside a branch. */
export function idsIn(steps: Step[], into: Set<string> = new Set()): Set<string> {
  for (const step of steps) {
    into.add(step.id);
    if (step.then) idsIn(step.then, into);
    if (step.else) idsIn(step.else, into);
  }

  return into;
}
